import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, BackHandler, Platform } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { RootStackParamList } from '../navigation/types';
import { RosterPicker, RosterStudentEntry } from '../components/RosterPicker';
import { useAuthStore } from '../store/useAuthStore';
import { useActivityStore } from '../store/useActivityStore';
import { useStrokeStore } from '../store/useStrokeStore';
import { StrokeSyncService } from '../services/StrokeSyncService';
import { TaskQueueService } from '../services/TaskQueueService';

type Props = StackScreenProps<RootStackParamList, 'RosterPick'>;

export function RosterPickScreen({ navigation, route }: Props) {
    const { classId, serverUrl } = route.params;
    const [joinError, setJoinError] = useState<string | null>(null);
    const [joiningStudentId, setJoiningStudentId] = useState<string | null>(null);

    // If device back button is pressed on roster, return to Home (leave class)
    // rather than falling back into any stale student session.
    useEffect(() => {
        if (Platform.OS === 'web') return;
        const onBackPress = () => {
            useAuthStore.getState().leaveClass();
            navigation.replace('Home');
            return true;
        };
        const subscription = BackHandler.addEventListener('hardwareBackPress', onBackPress);
        return () => subscription.remove();
    }, [navigation]);

    const handleSelectStudent = async (entry: RosterStudentEntry) => {
        if (joiningStudentId) return;
        setJoinError(null);
        setJoiningStudentId(entry.id);
        useActivityStore.getState().resetActivities();
        useStrokeStore.getState().clearStrokes();

        if (entry.isGroup) {
            useAuthStore.getState().joinAsGroup(classId, entry.id, entry.name, serverUrl);
            StrokeSyncService.startSync();
            navigation.replace('TaskQueue');
            setJoiningStudentId(null);
            return;
        }

        try {
            const resolvedId = entry.id || await TaskQueueService.resolveStudentId(entry.name, classId, serverUrl);
            if (!resolvedId) throw new Error('Unable to resolve student');

            await TaskQueueService.syncActiveTasks(resolvedId, classId, serverUrl);
            const tasks = await TaskQueueService.fetchPendingTasks(resolvedId, serverUrl);
            useActivityStore.getState().setStudentId(resolvedId);
            useAuthStore.getState().joinClass(classId, entry.name, resolvedId, serverUrl, entry.keyStage);
            useActivityStore.getState().loadPreFetchedTasks(tasks);
            StrokeSyncService.startSync();
            navigation.replace('TaskQueue');
        } catch (error) {
            console.error('[RosterPickScreen] Unable to load student tasks:', error);
            setJoinError("Let's try again! Tap your name when you're ready.");
        } finally {
            setJoiningStudentId(null);
        }
    };

    const handleJoinWithName = async (name: string) => {
        const cleanName = name.trim();
        if (!cleanName) return;
        setJoinError(null);
        useActivityStore.getState().resetActivities();
        useStrokeStore.getState().clearStrokes();

        try {
            const result = await TaskQueueService.resolveOrCreateStudent(cleanName, classId, serverUrl);
            if (!result) throw new Error('Unable to resolve or create student');

            const tasks = await TaskQueueService.fetchPendingTasks(result.studentId, serverUrl);
            useAuthStore.getState().joinClass(classId, cleanName, result.studentId, serverUrl, 'KS1');
            useActivityStore.getState().setStudentId(result.studentId);
            useActivityStore.getState().loadPreFetchedTasks(tasks);
            StrokeSyncService.startSync();
            navigation.replace('TaskQueue');
        } catch (error) {
            console.error('[RosterPickScreen] Unable to join class:', error);
            setJoinError("Let's try again! Check your connection or ask your teacher.");
        }
    };

    return (
        <View style={styles.container} accessibilityLabel="Roster pick screen" accessibilityRole="none">
            <RosterPicker
                classId={classId}
                syncServerUrl={serverUrl}
                isJoining={joiningStudentId !== null}
                joiningStudentId={joiningStudentId}
                onSelectStudent={handleSelectStudent}
                onTypeManually={(initialName) => {
                    if (initialName && initialName.trim()) {
                        handleJoinWithName(initialName.trim());
                    } else {
                        navigation.navigate('NameEntry', { classId, serverUrl });
                    }
                }}
            />
            {joinError ? <Text style={styles.joinError} accessibilityLiveRegion="polite" allowFontScaling={true} maxFontSizeMultiplier={1.5}>{joinError}</Text> : null}
        </View>
    );
}

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: '#E6F7FF' },
    joinError: { color: '#92400E', fontSize: 16, fontWeight: '600', textAlign: 'center', padding: 16 }
});
